import type { ChargerFlowNodes } from '@models/chargerFlows';
import type {
  ConnectorType,
  DefaultNodeData,
  NodeItem,
  ParentNode,
  TestGroupOptions,
} from '@models/flows';
import type { PaymentErrorCode } from '@models/paymentErrorCodes';
import type { Setting } from '@models/setting';
import { NodeTypes } from '@features/chargerFlows/validation/node-types';
import { isNodeType, isValueArray } from '@features/chargerFlows/validation/validation-helpers';

export { NodeTypes };

export const Connector: { [key: string]: ConnectorType } = {
  Source: 'source',
  Target: 'target',
};

export const TEST_GROUPS: TestGroupOptions[] = [
  { title: '1', group: 1 },
  { title: '2', group: 2 },
  { title: '3', group: 3 },
  { title: '4', group: 4 },
  { title: '5', group: 5 },
  { title: '6', group: 6 },
  { title: '7', group: 7 },
  { title: '8', group: 8 },
  { title: '9', group: 9 },
  { title: '10', group: 10 },
];

export const isNewNodeItem = (currentParentNode: ParentNode, nodeItem: NodeItem): boolean => {
  const parentNodeData = currentParentNode.data as unknown as DefaultNodeData;
  const parentNodeItems = parentNodeData?.items || [];
  if (parentNodeItems.length === 0) {
    return true;
  }
  if (parentNodeItems.every((item) => item.id !== nodeItem.id)) {
    return true;
  }
  if (isNodeType(currentParentNode.type, NodeTypes.errorNode)) {
    return !nodeItem.errorId || !nodeItem.label;
  }
  if (isNodeType(currentParentNode.type, NodeTypes.testGroupsNode)) {
    if (isValueArray(nodeItem.value)) {
      return (nodeItem.value as string[]).length === 0;
    }

    return false;
  }
  if (isNodeType(currentParentNode.type, NodeTypes.recurringActionNode)) {
    return !nodeItem.action;
  }
  if (isNodeType(currentParentNode.type, NodeTypes.successNode)) {
    return false;
  }
  if (isNodeType(currentParentNode.type, NodeTypes.debtCollectionNode)) {
    return false;
  }

  return !nodeItem.code && !nodeItem.label;
};

export const isDuplicateItem = (parentNode: ParentNode, newItem: NodeItem): boolean => {
  const parentNodeData = parentNode.data as unknown as DefaultNodeData;
  const parentNodeItems = parentNodeData?.items || [];
  const parentNodeType = parentNode.type as string;

  if (isNodeType(parentNodeType, NodeTypes.testGroupsNode)) {
    // Check if the parentNodeItems value array contains the newItem value
    if (isValueArray(newItem.value)) {
      if (isAllTestGroupsSelected(newItem.isAllSelected as boolean)) {
        return false;
      }

      return parentNodeItems.some(
        (item) =>
          isValueArray(item.value) &&
          item.id !== newItem.id &&
          (item.value as string[]).some((val) => (newItem.value as string[]).includes(val)),
      );
    }

    // If newItem value is not an array, check for direct match
    return parentNodeItems.some((item) => item.value === newItem.value);
  }
  if (isNodeType(parentNodeType, NodeTypes.errorNode)) {
    return parentNodeItems.some((item) => item.errorId === newItem.errorId);
  }
  if (isNodeType(parentNodeType, NodeTypes.conditionalNode, NodeTypes.transactionNatureNode)) {
    return parentNodeItems.some((item) => item.id !== newItem.id && item.code === newItem.code);
  }

  return false;
};

export const getLeftOverTestGroupsOptions = (parentNode: ParentNode, currentNode: NodeItem) => {
  const parentItems = parentNode.data as unknown as { items: NodeItem[] };

  const selectedItemsArray =
    parentItems?.items.map((item) => {
      if (item.id === currentNode.id && parentItems.items.length === 1 && item.isAllSelected) {
        // Move to next iteration
        return [];
      }

      if (!item.value || !Array.isArray(item.value)) {
        return [];
      }

      const values = (item.value as number[]).map((val) => Number(val));
      if (values.length === 0) {
        return [];
      }

      return values;
    }) || [];
  const selectedOptionNumbers = selectedItemsArray.flat();

  return TEST_GROUPS.filter((option) => !selectedOptionNumbers.includes(option.group));
};

export const normalizeTestGroupValue = (value: NodeItem['value']) => {
  // So above im sanitizeValues, but want to bring them bakc to normal object
  if (Array.isArray(value)) {
    return TEST_GROUPS.filter((option) => (value as number[]).includes(option.group));
  }
  return [];
};

export const isAllTestGroupsSelected = (isAllSelected: boolean): boolean => {
  return isAllSelected === true;
};
// End: TestGroups component helpers

export const getFlowIdFromUrl = (): string => {
  const pathName = window.location.pathname.split('/dashboard/charger-flows/')[1].split('/edit')[0];

  return pathName || '';
};

export const normalizeErrorsForNode = (errors: PaymentErrorCode[]): NodeItem[] => {
  return errors.map((error) => ({
    id: error.id,
    label: error.error,
  }));
};

export const normalizeSettingsForNode = (settings: Setting[]): NodeItem[] => {
  return settings.map((setting) => ({
    id: setting.id,
    label: setting.slug,
  }));
};

export const sanitizeNodes = (nodeItems: ChargerFlowNodes) => {
  const sanitizedNodes = nodeItems.map((item) => {
    const { id, type, data, position } = item;
    return { id, type, data, position };
  });

  return sanitizedNodes;
};

export const renderRetryInterval = (retryInterval: {
  days: number;
  hours: number;
  minutes: number;
}) => {
  const { days, hours, minutes } = retryInterval;
  const parts = [];

  if (days > 0) {
    parts.push(`${days} day${days > 1 ? 's' : ''}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
  }

  return parts.length > 0 ? parts.join(', ') : '0 minutes';
};
